import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Investment } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { SITE_GLASS_CARD, SITE_GLASS_TILE, SITE_FORM_CONTROL } from '../constants/siteUi';

export function InvestorDashboard() {
  const { session, profile, refreshProfile } = useAuth();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [msg, setMsg] = useState<string | null>(null);

  const token = session?.access_token;

  useEffect(() => {
    setFullName(profile?.full_name ?? '');
  }, [profile?.full_name]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const res = await apiFetch<{ investments: Investment[] }>('/api/my-investments', { token });
      if (!cancelled) setInvestments(res.investments);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const totals = useMemo(() => {
    const committed = investments
      .filter((i) => i.status !== 'cancelled')
      .reduce((s, i) => s + Number(i.amount), 0);
    return { committed, count: investments.length };
  }, [investments]);

  async function onSaveProfile(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setMsg(null);
    await apiFetch('/api/me', {
      method: 'PATCH',
      token,
      body: JSON.stringify({ full_name: fullName }),
    });
    await refreshProfile();
    setMsg('Profile updated');
  }

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(260px,20rem)]">
      <section className="flex min-w-0 flex-col gap-8">
        <div className={`${SITE_GLASS_CARD} space-y-3 p-6 md:p-8`}>
          <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
            Overview
          </p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
            Investor cockpit
          </h1>
          <p className="font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
            Portfolio figures reflect recorded commitments in this workspace — wire instructions ship with your
            subscription documents.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className={`${SITE_GLASS_TILE} p-5`}>
            <p className="font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Total committed
            </p>
            <p className="font-display mt-2 text-2xl font-semibold text-[var(--home-text-on-dark)]">
              ${totals.committed.toLocaleString()}
            </p>
          </div>
          <div className={`${SITE_GLASS_TILE} p-5`}>
            <p className="font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Positions
            </p>
            <p className="font-display mt-2 text-2xl font-semibold text-[var(--home-text-on-dark)]">{totals.count}</p>
          </div>
          <div className={`${SITE_GLASS_TILE} p-5`}>
            <p className="font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Approval
            </p>
            <p className="mt-2 font-display text-lg font-semibold text-emerald-300/95">
              {profile?.investor_approved ? 'Cleared' : 'Pending'}
            </p>
          </div>
        </div>

        <div className={`${SITE_GLASS_CARD} overflow-hidden p-6 md:p-8`}>
          <h2 className="font-display text-lg font-bold text-[var(--home-text-on-dark)]">Investments</h2>
          <div className="mt-5 overflow-hidden rounded-2xl border border-white/[0.08] ring-1 ring-white/[0.04]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--home-zone-dark)]/80 text-left font-mono text-[0.65rem] uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                <tr>
                  <th className="px-4 py-3">Deal</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="text-[var(--home-text-on-dark)]">
                {investments.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/[0.06] bg-[var(--home-zone-dark)]/25">
                    <td className="px-4 py-3 font-mono text-xs text-[var(--home-muted-on-dark)]">{inv.deal_id.slice(0, 8)}…</td>
                    <td className="px-4 py-3">${Number(inv.amount).toLocaleString()}</td>
                    <td className="px-4 py-3 capitalize text-emerald-300/90">{inv.status.replace('_', ' ')}</td>
                  </tr>
                ))}
                {investments.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-[var(--home-muted-on-dark)]">
                      No commitments yet —{' '}
                      <Link to="/dashboard/offerings" className="font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline">
                        browse offerings
                      </Link>
                      .
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <aside className="flex min-w-0 flex-col gap-6">
        <div className={`${SITE_GLASS_CARD} space-y-4 p-6`}>
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.08em] text-[var(--home-text-on-dark)]">
            Profile
          </h2>
          <form className="space-y-4" onSubmit={onSaveProfile}>
            <label className="block space-y-2 font-body text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Legal name</span>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <button
              type="submit"
              className="w-full rounded-xl bg-[var(--home-miami-blue)] px-4 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105"
            >
              Save
            </button>
            {msg ? <p className="font-body text-xs text-emerald-300/95">{msg}</p> : null}
          </form>
        </div>
        <div className={`${SITE_GLASS_CARD} space-y-2 p-5`}>
          <p className="font-display text-xs font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]">
            Document center
          </p>
          <p className="font-body text-xs leading-relaxed text-[var(--home-muted-on-dark)]">
            After staff attach files to your record, downloads will surface here via{' '}
            <code className="rounded bg-black/35 px-1 py-0.5 font-mono text-[10px] text-[var(--home-text-on-dark)]/90">
              /api/documents/signed-url
            </code>{' '}
            using your session.
          </p>
        </div>
      </aside>
    </div>
  );
}
